import React, { useState } from "react";
import moment from "moment";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { ToggleButton } from "../../../components/Button";
import { ReportFilterValue } from "../components/NewReportFilters";
import { useReportQuery } from "../shared/useReportQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { centsToUsd, formatMetric, METRIC_LABELS } from "../shared/format";
import {
  AGGREGATE_SERIES_KEY,
  buildHourlySeries,
  DAY_SERIES_COLORS,
  formatByDayGridRows,
  MAX_BY_DAY_HOURLY_DAYS,
  zeroFillHourlyRows,
} from "../shared/hourlySeries";
import { MetricKey, ReportType } from "../api/types";

const CARDS: MetricKey[] = ["NetSales", "Gross", "OrderCount"];

type CompareMode = "none" | "prevPeriod" | "prevYear";
const COMPARE_OPTIONS: { value: CompareMode; label: string }[] = [
  { value: "none", label: "No comparison" },
  { value: "prevPeriod", label: "vs previous period" },
  { value: "prevYear", label: "vs previous year" },
];

// "By day" = the old report's layout (one line per day, engine dimension=Day);
// "Combined" = one aggregate 24-hour profile, where the Compare overlay applies.
type ViewMode = "byDay" | "combined";

// Per-channel config. The all-channel and ticketing pages are identical except for the report-type pair
// (the ticketing types force OrderType = Ticketing server-side), the title/tooltip, and the export prefix.
export interface NetHourlyConfig {
  title: string;
  tooltipText: string;
  perHourType: ReportType; // "SalesByHour" | "TicketingNetHourly"
  cumulativeType: ReportType; // "CumulativeHourly" | "TicketingCumulativeHourly"
  exportPrefix: string;
}

const ALL_TOOLTIP =
  "Net Sales by hour on the full business day (boundary to boundary, $0 hours included). " +
  "By day draws one line per day like the old report; Combined aggregates the window into a " +
  "single profile and supports comparing against the previous period/year. The old report shows " +
  "Net only — Gross stays hidden unless you toggle it on. Cumulative shows running totals " +
  "(per day in By day view). Net Sales is the real-money net that matches the Financial reports.";

export const NET_HOURLY_ALL: NetHourlyConfig = {
  title: "Net Hourly",
  tooltipText: ALL_TOOLTIP,
  perHourType: "SalesByHour",
  cumulativeType: "CumulativeHourly",
  exportPrefix: "NetHourly",
};

export const NET_HOURLY_TICKETING: NetHourlyConfig = {
  title: "Ticketing Net Hourly",
  tooltipText:
    "The legacy Ticketing Net Hourly Sales Report from the NEW engine — Net Sales by hour, restricted " +
    "to ticketing-channel orders (OrderType = Ticketing, forced server-side). By day draws one line per " +
    "day like the old report (days as rows, the 5 AM operating-day hours as columns); Combined aggregates " +
    "the window into a single profile. Net Sales is the CANONICAL real-money net that matches the Financial " +
    "reports (shared with Net Hourly — the legacy old-net formula is NOT used). Cumulative shows running " +
    "totals (per day in By day view). Gross stays hidden unless you toggle it on.",
  perHourType: "TicketingNetHourly",
  cumulativeType: "TicketingCumulativeHourly",
  exportPrefix: "TicketingNetHourly",
};

// Shift the window back by one year, or by its own length (previous period).
function shiftFilters(f: ReportFilterValue, mode: CompareMode): ReportFilterValue | null {
  if (mode === "none") return null;
  const from = moment(f.from, "YYYY-MM-DD");
  const to = moment(f.to, "YYYY-MM-DD");
  if (!from.isValid() || !to.isValid()) return null;
  if (mode === "prevYear") {
    return {
      ...f,
      from: from.clone().subtract(1, "year").format("YYYY-MM-DD"),
      to: to.clone().subtract(1, "year").format("YYYY-MM-DD"),
    };
  }
  const days = to.diff(from, "days") + 1;
  return {
    ...f,
    from: from.clone().subtract(days, "days").format("YYYY-MM-DD"),
    to: to.clone().subtract(days, "days").format("YYYY-MM-DD"),
  };
}

// Days in the window, inclusive (mirrors the engine's ResolveDayRange span check).
function windowDays(f: ReportFilterValue): number {
  const from = moment(f.from, "YYYY-MM-DD");
  const to = moment(f.to, "YYYY-MM-DD");
  if (!from.isValid() || !to.isValid()) return 0;
  return to.diff(from, "days") + 1;
}

// Net Hourly — in Combined the cumulative toggle swaps SalesByHour <-> CumulativeHourly (identical
// params); the By day view adds dimension=Day (one zero-filled 24-row block per day, per the
// sprint-2 contract) and derives its cumulative curves client-side, resetting per day (RP-2175 QA:
// the engine returns dimension=Day rows unaccumulated).
export default function NetHourlyReport() {
  return <NetHourlyReportView config={NET_HOURLY_ALL} />;
}

export function NetHourlyReportView({ config }: { config: NetHourlyConfig }) {
  const [filters, setFilters] = useState(defaultFilters);
  const [cumulative, setCumulative] = useState(false);
  const [compare, setCompare] = useState<CompareMode>("none");
  const [view, setView] = useState<ViewMode>("byDay");
  // Display-only (RP-2163): the old report shows Net only, so Gross is opt-in. Not part of the
  // search gate — flipping it never refetches. On CumulativeHourly the engine SUPPRESSES Gross
  // from metrics[] but still sends it in every row's values — the toggle opts back in explicitly.
  const [showGross, setShowGross] = useState(false);

  // Fetch only on Search; the snapshot carries the toggles too, so the chart/table reflect what
  // was submitted rather than the pending draft.
  const { submitted, isDirty, hasRun, search } = useSearchGate({
    filters,
    cumulative,
    compare,
    view,
  });
  const activeCumulative = submitted?.cumulative ?? cumulative;
  const activeCompare = submitted?.compare ?? compare;
  const activeView = submitted?.view ?? view;
  const byDay = activeView === "byDay";
  // Combined leaves the running total to the engine (CumulativeHourly). By day ALWAYS fetches the
  // per-hour type and builds the running total client-side: the engine does not accumulate on
  // dimension=Day responses, so "By day + Cumulative" used to render plain per-hour values. Summing
  // the same per-hour Net per day is exactly what cumulative means here — no figure changes, and
  // there is no double-accumulation risk because the fetched rows are always per-hour.
  const clientRunningTotal = activeCumulative && byDay;
  const reportType = activeCumulative && !byDay ? config.cumulativeType : config.perHourType;
  // The mode the user asked for — names the CSV export; it is not what gets fetched in By day.
  const modeType = activeCumulative ? config.cumulativeType : config.perHourType;

  // The by-day shape zero-fills 24 rows per day and the engine caps it (HTTP 400 past the cap) —
  // pre-guard client-side with the same rule so the user gets the message before a doomed call.
  const submittedDays = submitted ? windowDays(submitted.filters) : 0;
  const spanTooLarge = byDay && submittedDays > MAX_BY_DAY_HOURLY_DAYS;

  const state = useReportQuery(
    submitted && !spanTooLarge
      ? toQueryParams(reportType, submitted.filters, byDay ? { dimension: "Day" } : undefined)
      : null
  );
  // Compare applies to the Combined profile only (the old per-day chart had no compare mode).
  const compareFilters =
    submitted && !byDay ? shiftFilters(submitted.filters, submitted.compare) : null;
  const compareState = useReportQuery(
    compareFilters ? toQueryParams(reportType, compareFilters) : null
  );

  const applyGrossToggle = (metrics: MetricKey[], hasGrossValues: boolean): MetricKey[] => {
    if (!showGross) return metrics.filter((m) => m !== "Gross");
    if (metrics.includes("Gross") || !hasGrossValues) return metrics;
    // Gross is computed-but-suppressed on CumulativeHourly (contract §3) — opt in after Net.
    const out = [...metrics];
    const i = out.indexOf("NetSales");
    out.splice(i >= 0 ? i + 1 : out.length, 0, "Gross");
    return out;
  };

  return (
    <ReportPageShell
      title={config.title}
      tooltipText={config.tooltipText}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      // With the fetch pre-guarded (spanTooLarge), the shell lands in its idle state — surface
      // the engine's own cap rule there instead of letting a doomed request 400.
      idlePrompt={
        spanTooLarge ? (
          <span className="font-medium text-amber-700">
            The by-day breakdown shows 24 rows per day and is capped at {MAX_BY_DAY_HOURLY_DAYS}{" "}
            days; this window spans {submittedDays}. Narrow the date range, or switch to Combined.
          </span>
        ) : undefined
      }
      filterVendor
      filterLocation
      actions={
        <>
          <ToggleButton isSelected={view === "byDay"} onClick={() => setView("byDay")}>
            By day
          </ToggleButton>
          <ToggleButton isSelected={view === "combined"} onClick={() => setView("combined")}>
            Combined
          </ToggleButton>
          <span className="mx-1 h-5 w-px bg-gray-200" />
          <ToggleButton isSelected={!cumulative} onClick={() => setCumulative(false)}>
            Per hour
          </ToggleButton>
          <ToggleButton isSelected={cumulative} onClick={() => setCumulative(true)}>
            Cumulative
          </ToggleButton>
          {view === "combined" && (
            <label className="ml-2 flex items-center gap-1 text-sm text-gray-600">
              Compare
              <select
                value={compare}
                onChange={(e) => setCompare(e.target.value as CompareMode)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                {COMPARE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="ml-2 flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showGross}
              onChange={(e) => setShowGross(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Show Gross
          </label>
        </>
      }
    >
      {(data) => {
        // One series per business day (By day) or a single aggregate profile (Combined), on the
        // fixed boundary→boundary 24-slot axis with $0 hours — the old report's layout.
        const series = buildHourlySeries(data, {
          cumulative: activeCumulative,
          runningTotal: clientRunningTotal,
        });
        const chartData = series.points;

        const withCompare = !series.perDay && activeCompare !== "none";
        if (withCompare && compareState.data) {
          // Slots align 1:1 — both series are boundary-rotated 24-hour profiles.
          const comparePoints = buildHourlySeries(compareState.data, {
            cumulative: activeCumulative,
          }).points;
          chartData.forEach((p, i) => {
            p.compare = (comparePoints[i]?.[AGGREGATE_SERIES_KEY] as number) ?? 0;
          });
        }
        const compareName = activeCompare === "prevYear" ? "Previous year" : "Previous period";

        // Grid mirrors the chart: by-day rows keep the engine's per-day blocks (Date column),
        // the combined profile shows every hour of the business day.
        const shaped = series.perDay
          ? formatByDayGridRows(data, { cumulative: clientRunningTotal })
          : zeroFillHourlyRows(data, { cumulative: activeCumulative });
        const hasGrossValues = data.rows.some((r) => r.values.Gross != null);
        const gridData = { ...shaped, metrics: applyGrossToggle(shaped.metrics, hasGrossValues) };

        return (
          <>
            <div className="mb-3 grid grid-cols-3 gap-3">
              {CARDS.filter((m) => data.metrics.includes(m) || data.totals[m] != null)
                .filter((m) => showGross || m !== "Gross")
                .map((m) => (
                  <StatCard
                    key={m}
                    label={METRIC_LABELS[m]}
                    value={formatMetric(m, data.totals[m])}
                    emphasis={m === "NetSales"}
                  />
                ))}
            </div>

            {activeCompare !== "none" && !series.perDay && compareState.error && (
              <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                Comparison series failed to load: {compareState.error}
              </div>
            )}

            <div className="mb-3 overflow-x-auto rounded-md bg-white p-2">
              <LineChart
                width={Math.max(720, 24 * 46)}
                height={340}
                data={chartData}
                margin={{ top: 16, right: 16, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  interval={1}
                  tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                />
                <YAxis
                  width={90}
                  tickFormatter={(v: number) => centsToUsd(v)}
                  tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                />
                <Tooltip formatter={(v: number) => centsToUsd(v)} />
                <Legend />
                {series.perDay ? (
                  // One line per business day — the old report's layout (fixed palette, day order).
                  series.seriesKeys.map((day, i) => (
                    <Line
                      key={day}
                      type="monotone"
                      dataKey={day}
                      name={day}
                      stroke={DAY_SERIES_COLORS[i % DAY_SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))
                ) : (
                  <Line
                    type="monotone"
                    dataKey={AGGREGATE_SERIES_KEY}
                    name={activeCumulative ? "Net (cumulative)" : "Net Sales"}
                    stroke="#B71C1C"
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {withCompare && (
                  <Line
                    type="monotone"
                    dataKey="compare"
                    name={compareName}
                    stroke="#6b7280"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    dot={false}
                  />
                )}
              </LineChart>
            </div>

            <MetricGrid
              data={gridData}
              keyColumnHeader="Hour"
              groupColumnHeader={series.perDay ? "Date" : undefined}
              // CSV rows are gridData, so the export carries whatever the grid shows (running
              // totals in cumulative mode); name it for the MODE, not the type actually fetched.
              exportName={`${config.exportPrefix}_${modeType}${byDay ? "_ByDay" : ""}`}
            />
          </>
        );
      }}
    </ReportPageShell>
  );
}
